/* eslint-disable @typescript-eslint/no-explicit-any */
import { create } from 'zustand';
import {
  CognitoIdentityProviderClient,
  AdminListGroupsForUserCommand,
  AdminListGroupsForUserCommandOutput,
  ListGroupsCommand,
  ListUsersCommand,
  ListGroupsCommandOutput,
  UserType,
} from '@aws-sdk/client-cognito-identity-provider';
import logger from '@/utils/logger';
import { Command } from '@smithy/smithy-client';
import { invokeUserManagerLambda } from './createUserLambda';
import type { UserManagerLambdaEvent } from '@amplify/functions/userManager/handler';
import { getAmplifyOutputs, getSession } from '@/utils/helpers';

const outputs = getAmplifyOutputs();
const UserPoolId = outputs.auth.user_pool_id;
const UserPool_region = outputs.auth.aws_region;
let globalCognitoClient: CognitoIdentityProviderClient | null = null;

// Initialize the Cognito client
async function setupCognitoClient() {
  const session = await getSession();
  if (!session.credentials) {
    throw new Error('Failed to initialize Cognito client, no AWS credentials available');
  }

  return new CognitoIdentityProviderClient({
    region: UserPool_region,
    credentials: {
      accessKeyId: session.credentials.accessKeyId,
      secretAccessKey: session.credentials.secretAccessKey,
      sessionToken: session.credentials.sessionToken,
    },
  });
}

// Runs the Cognito commands and returns the response. If the client is not initialized, it will be initialized first.
export const executeCognitoCommand = async <T extends Command<any, any, any>>(
  command: T
): Promise<{
  resp: (T extends Command<any, infer Output, any> ? Output : never) | undefined;
  errorMessage: string;
}> => {
  let errorMessage = '';

  if (!globalCognitoClient) {
    try {
      globalCognitoClient = await setupCognitoClient();
    } catch (error) {
      typeof error === 'string' ? (errorMessage = error) : `Error initializing Cognito client: ${error}`;

      return { resp: undefined, errorMessage };
    }
  }

  try {
    const resp = await globalCognitoClient.send(command);
    return { resp, errorMessage: '' };
  } catch (error) {
    errorMessage = `Fehler beim Ausführen des Cognito-Befehls`;
    typeof error === 'string' && (errorMessage = error);

    return { resp: undefined, errorMessage };
  }
};

type UseCognitoClient = {
  _isInitialized: boolean;
  loading: boolean;
  errorMesssage: string;
  userList: UserType[] | undefined;
  groupList: NonNullable<ListGroupsCommandOutput['Groups']> | [];
  userGroupsMapping: Array<AdminListGroupsForUserCommandOutput & { username: string }> | [];
  _init: () => Promise<void>;
  getGroupsByUsername: (username: string) => Promise<AdminListGroupsForUserCommandOutput | undefined>;
  createUser: (userEmail: string, scoutAccess: boolean, clubAccess: boolean) => Promise<void>;
  deleteUser: (userEmail: string) => Promise<void>;
  addUserToGroup: (userEmail: string, groupName: string) => Promise<void>;
  removeUserFromGroup: (userEmail: string, groupName: string) => Promise<void>;
  changeUserAccess: (userEmail: string, scoutAccess: boolean, clubAccess: boolean) => Promise<void>;
};

export const useCognitoContext = create<UseCognitoClient>((set, get) => {
  // set an error message as state if an error occurs during the cognito operation
  const _handleErrors = (error: string) => {
    set((state) => ({ ...state, errorMesssage: error }));
    logger.error('Cognito error:', error);
  };

  // Get all users in the Cognito
  const getUserList = async () => {
    const command = new ListUsersCommand({
      UserPoolId: UserPoolId,
    });

    const userList = await executeCognitoCommand(command);

    if (userList.errorMessage || !userList.resp) {
      _handleErrors(userList.errorMessage);
    }

    const filteredUsers = userList.resp?.Users?.filter((user) => {
      const emailAttr = user.Attributes?.find((attr) => attr.Name === 'email');
      const email = emailAttr?.Value || '';

      return !email.startsWith('tech_') && !email.startsWith('test_') && !email.startsWith('demo_');
    });

    return filteredUsers;
  };

  // List all groups in the Cognito user pool
  const listGroups = async () => {
    const command = new ListGroupsCommand({ UserPoolId: UserPoolId });
    const groups = await executeCognitoCommand(command);

    if (groups.errorMessage || !groups.resp) {
      _handleErrors(groups.errorMessage);
      return;
    }

    return groups.resp.Groups?.length ? groups.resp.Groups : [];
  };

  // Get all groups for multiple users
  const getGroupsByMultipleUsernames = async () => {
    const userList = await getUserList();

    if (!userList) {
      const errorMesssage = 'Keine Benutzer gefunden';
      set((state) => ({ ...state, errorMesssage }));
    }

    const userMails =
      userList!.map((user) => user.Attributes?.find((attr) => attr.Name === 'email')?.Value || '') || [];

    const userGroups = await Promise.all(
      userMails.map(async (userMail) => {
        const groupsCommand = new AdminListGroupsForUserCommand({
          UserPoolId: UserPoolId,
          Username: userMail,
        });
        const userGroups = await executeCognitoCommand(groupsCommand);

        if (userGroups.errorMessage || !userGroups.resp) {
          _handleErrors(userGroups.errorMessage);
          return;
        }

        return { ...userGroups.resp, username: userMail };
      })
    );

    const nonEmptyUserGroups = userGroups.filter((userGroup) => userGroup) as (AdminListGroupsForUserCommandOutput & {
      username: string;
    })[];

    return nonEmptyUserGroups;
  };

  const _setLoading = (loading: boolean) => {
    const currentErrorMesssage = get().errorMesssage;
    set((state) => ({ ...state, loading, errorMesssage: loading ? '' : currentErrorMesssage }));
  };

  // Execute the user change (create/delete user or add/remove user from group)
  // and reinitialize the cognito client to get the latest data
  const _executeUserChange = async (params: UserManagerLambdaEvent) => {
    const res = await invokeUserManagerLambda(params);
    if (!res.isOK) {
      _handleErrors(res.message);
      logger.error(res.errorMessage);
      return;
    }

    await get()._init();
  };

  return {
    _isInitialized: false,
    loading: false,
    errorMesssage: '',
    userList: undefined,
    groupList: [],
    userGroupsMapping: [],

    _init: async () => {
      _setLoading(true);

      const userListPromise = getUserList();
      const groupListPromise = listGroups();
      const userWithGroupsPromise = getGroupsByMultipleUsernames();

      try {
        const [userList, groupList, userGroupsMapping] = await Promise.all([
          userListPromise,
          groupListPromise,
          userWithGroupsPromise,
        ]);

        set((state) => ({
          ...state,
          userList,
          groupList,
          userGroupsMapping,

          isInitialized: true,
          errorMesssage: '',
        }));
      } catch (error) {
        let errorMessage = 'Fehler beim Initialisieren des Cognito-Clients';
        typeof error === 'string' && (errorMessage = error);

        logger.error('Error initializing Cognito client:', error);

        _handleErrors(errorMessage);
      } finally {
        _setLoading(false);
      }
    },

    // Get all groups for a user
    getGroupsByUsername: async (userMail: string) => {
      const groupsCommand = new AdminListGroupsForUserCommand({
        UserPoolId: UserPoolId,
        Username: userMail,
      });
      const userGroups = await executeCognitoCommand(groupsCommand);

      if (userGroups.errorMessage || !userGroups.resp) {
        _handleErrors(userGroups.errorMessage);
        return;
      }
      return userGroups.resp;
    },

    // Create a new user in the Cognito user pool
    createUser: async (userEmail: string, clubAccess: boolean, scoutAccess: boolean) => {
      _setLoading(true);

      await _executeUserChange({
        email: userEmail,
        action: 'add',
        clubAccess,
        scoutAccess,
        userManagementAction: 'userChange',
      });
      _setLoading(false);
    },

    // Delete a user from the Cognito
    deleteUser: async (userEmail: string) => {
      _setLoading(true);

      await _executeUserChange({
        email: userEmail,
        action: 'remove',
        userManagementAction: 'userChange',
        clubAccess: false,
        scoutAccess: false,
      });
      _setLoading(false);
    },

    // Add a user to a group
    addUserToGroup: async (userEmail: string, groupName: string) => {
      _setLoading(true);

      await _executeUserChange({
        email: userEmail,
        group: groupName,
        action: 'add',
        userManagementAction: 'userGroupChange',
      });
      _setLoading(false);
    },

    // Remove a user from a group
    removeUserFromGroup: async (userEmail: string, groupName: string) => {
      _setLoading(true);

      await _executeUserChange({
        email: userEmail,
        group: groupName,
        action: 'remove',
        userManagementAction: 'userGroupChange',
      });
      _setLoading(false);
    },

    changeUserAccess: async (userEmail: string, scoutAccess: boolean, clubAccess: boolean) => {
      _setLoading(true);

      await _executeUserChange({
        email: userEmail,
        scoutAccess,
        clubAccess,
        userManagementAction: 'userAccessChange',
      });
      _setLoading(false);
    },
  };
});
